import React,{useEffect,useMemo,useState}from"react";
import{Box,Paper,Typography,Button,TextField,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TablePagination,useMediaQuery,FormControl,InputLabel,Select,MenuItem,Dialog,DialogTitle,DialogContent,DialogActions,Grid,Divider}from"@mui/material";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAlert}from"../../context/AlertContext";
import{useAuth}from"../../context/AuthContext";

function formatMoney(value){
  const number=Number(value||0);
  return number.toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2});
}

function formatDateTime(value){
  if(!value)return"-";
  const date=new Date(value);
  if(Number.isNaN(date.getTime()))return"-";
  return date.toLocaleString("es-EC",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

export default function AdminCash(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showError}=useAlert();
  const{user}=useAuth();
  const isMobile=useMediaQuery("(max-width:900px)");
  const token=localStorage.getItem("accessToken");
  const canView=userCan(user,"ventas.view");

  const[cajas,setCajas]=useState([]);
  const[loading,setLoading]=useState(false);
  const[search,setSearch]=useState("");
  const[estado,setEstado]=useState("todos");
  const[fechaDesde,setFechaDesde]=useState("");
  const[fechaHasta,setFechaHasta]=useState("");
  const[page,setPage]=useState(0);
  const rowsPerPage=isMobile?10:30;

  const[detailOpen,setDetailOpen]=useState(false);
  const[detailLoading,setDetailLoading]=useState(false);
  const[selectedCaja,setSelectedCaja]=useState(null);
  const[movimientos,setMovimientos]=useState([]);

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:0.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none",
        "& .MuiMenu-list":{p:0.5},
        "& .MuiMenuItem-root":{
          fontSize:14,
          borderRadius:"7px",
          color:dashboardTheme.text_color,
          "&:hover":{bgcolor:dashboardTheme.dashboard_background},
          "&.Mui-selected":{bgcolor:dashboardTheme.dashboard_background},
          "&.Mui-selected:hover":{bgcolor:dashboardTheme.dashboard_background},
        },
      },
    },
  };

  const selectSx={
    minWidth:{xs:"100%",sm:150},
    ...dashboardTheme.inputStyles,
    "& .MuiSelect-icon":{color:dashboardTheme.text_color},
  };

  const loadCajas=async()=>{
    if(!canView)return;
    try{
      setLoading(true);
      const params=new URLSearchParams();
      if(search.trim())params.append("q",search.trim());
      if(estado!=="todos")params.append("estado",estado);
      if(fechaDesde)params.append("fecha_desde",fechaDesde);
      if(fechaHasta)params.append("fecha_hasta",fechaHasta);
      params.append("limit","1000");

      const response=await fetch(`/api/ventas/caja/listar/?${params.toString()}`,{
        headers:{Authorization:`Bearer ${token}`}
      });
      const data=await response.json();
      if(!response.ok){
        showError(data.error||data.detail||"No se pudieron cargar las cajas");
        return;
      }
      setCajas(data.results||[]);
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  const loadDetalle=async(cajaId)=>{
    try{
      setDetailLoading(true);
      setSelectedCaja(null);
      setMovimientos([]);
      setDetailOpen(true);

      const response=await fetch(`/api/ventas/caja/${cajaId}/`,{
        headers:{Authorization:`Bearer ${token}`}
      });
      const data=await response.json();
      if(!response.ok){
        showError(data.error||data.detail||"No se pudo cargar el detalle de la caja");
        setDetailOpen(false);
        return;
      }
      setSelectedCaja(data.caja||null);
      setMovimientos(data.movimientos||[]);
    }catch{
      showError("Error de conexión");
      setDetailOpen(false);
    }finally{
      setDetailLoading(false);
    }
  };

  useEffect(()=>{
    const delay=setTimeout(()=>{
      setPage(0);
      loadCajas();
    },350);
    return()=>clearTimeout(delay);
  },[search,estado,fechaDesde,fechaHasta,canView]);

  useEffect(()=>{
    setPage(0);
  },[rowsPerPage]);

  const visibleCajas=useMemo(()=>{
    const start=page*rowsPerPage;
    return cajas.slice(start,start+rowsPerPage);
  },[cajas,page,rowsPerPage]);

  const totals=useMemo(()=>{
    return cajas.reduce((acc,caja)=>{
      acc.ventas+=Number(caja.total_ventas_sistema||0);
      acc.efectivo+=Number(caja.total_efectivo_sistema||0);
      acc.transferencia+=Number(caja.total_transferencia_sistema||0);
      if(caja.estado==="CERRADA"&&caja.usuario_cierre&&caja.efectivo_contado_cierre!==null&&caja.efectivo_contado_cierre!==undefined){
        acc.diferencia+=Number(caja.diferencia_efectivo||0);
      }
      return acc;
    },{ventas:0,efectivo:0,transferencia:0,diferencia:0});
  },[cajas]);

  const secondaryText=dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#64748b";

  const infoLabelSx={fontSize:11.5,fontWeight:500,color:secondaryText,lineHeight:1.3};
  const infoValueSx={fontSize:14,fontWeight:600,color:dashboardTheme.text_color,lineHeight:1.35,mt:0.25};

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:{xs:1.5,md:2}}}>
              <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>Cajas</Typography>
            </Box>

            <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr 1fr",md:"repeat(4,1fr)"},gap:1,mb:1.5}}>
              {[
                ["Ventas",totals.ventas],
                ["Efectivo",totals.efectivo],
                ["Transferencias",totals.transferencia],
                ["Diferencia",totals.diferencia],
              ].map(([label,value])=>(
                <Paper key={label} elevation={0} sx={{p:1.4,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background,boxShadow:"none"}}>
                  <Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText}}>{label}</Typography>
                  <Typography sx={{fontSize:{xs:16,md:18},fontWeight:700,color:label==="Diferencia"&&Number(value)<0?"#D32F2F":dashboardTheme.primary_color,mt:0.2}}>${formatMoney(value)}</Typography>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={{...dashboardTheme.tableStyles.toolbar,display:"flex",flexWrap:"wrap",alignItems:"center",gap:1}}>
                <TextField fullWidth size="small" label="Buscar caja, usuario u observación" value={search} onChange={e=>setSearch(e.target.value)} sx={{maxWidth:{xs:"100%",md:320},...dashboardTheme.inputStyles}}/>
                <TextField size="small" type="date" label="Desde" value={fechaDesde} onChange={e=>setFechaDesde(e.target.value)} InputLabelProps={{shrink:true}} sx={{minWidth:{xs:"100%",sm:150},...dashboardTheme.inputStyles}}/>
                <TextField size="small" type="date" label="Hasta" value={fechaHasta} onChange={e=>setFechaHasta(e.target.value)} InputLabelProps={{shrink:true}} sx={{minWidth:{xs:"100%",sm:150},...dashboardTheme.inputStyles}}/>
                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Estado</InputLabel>
                  <Select label="Estado" value={estado} onChange={e=>setEstado(e.target.value)} MenuProps={selectMenuProps}>
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="ABIERTA">Abierta</MenuItem>
                    <MenuItem value="CERRADA">Cerrada</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Caja</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:170}}>Usuario</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:145}}>Apertura</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Estado</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Inicial</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Ventas</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Efectivo</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Transfer.</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Diferencia</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleCajas.map(caja=>(
                        <TableRow key={caja.id} hover sx={{...dashboardTheme.tableStyles.bodyRow,cursor:"pointer"}} onDoubleClick={()=>loadDetalle(caja.id)}>
                          <TableCell>
                            <Typography sx={{fontSize:13.5,fontWeight:700,color:dashboardTheme.primary_color}}>#{caja.id}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{fontSize:13.5,fontWeight:600,color:dashboardTheme.text_color}}>{caja.usuario_apertura_nombre||"-"}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{fontSize:13,fontWeight:500,color:dashboardTheme.text_color}}>{formatDateTime(caja.fecha_apertura)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{fontSize:13.5,fontWeight:600,color:caja.estado==="ABIERTA"?"#1B7F4C":dashboardTheme.text_color}}>{caja.estado}</Typography>
                          </TableCell>
                          <TableCell align="right"><Typography sx={{fontSize:13.5,fontWeight:600}}>${formatMoney(caja.monto_inicial)}</Typography></TableCell>
                          <TableCell align="right"><Typography sx={{fontSize:13.5,fontWeight:700,color:dashboardTheme.primary_color}}>${formatMoney(caja.total_ventas_sistema)}</Typography></TableCell>
                          <TableCell align="right"><Typography sx={{fontSize:13.5,fontWeight:600}}>${formatMoney(caja.total_efectivo_sistema)}</Typography></TableCell>
                          <TableCell align="right"><Typography sx={{fontSize:13.5,fontWeight:600}}>${formatMoney(caja.total_transferencia_sistema)}</Typography></TableCell>
                          <TableCell align="right">
                            <Typography sx={{fontSize:13.5,fontWeight:700,color:Number(caja.diferencia_efectivo||0)<0?"#D32F2F":dashboardTheme.text_color}}>
                              {caja.estado==="ABIERTA"||!caja.usuario_cierre||caja.efectivo_contado_cierre===null||caja.efectivo_contado_cierre===undefined?"-":`$${formatMoney(caja.diferencia_efectivo)}`}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button variant="outlined" size="small" onClick={()=>loadDetalle(caja.id)} sx={{minHeight:32,px:1.5,borderRadius:"8px",textTransform:"none",fontWeight:600,color:dashboardTheme.button_color,borderColor:dashboardTheme.button_color,boxShadow:"none","&:hover":{borderColor:dashboardTheme.button_color,bgcolor:`${dashboardTheme.button_color}12`,boxShadow:"none"}}}>Ver</Button>
                          </TableCell>
                        </TableRow>
                      ))}

                      {!loading&&visibleCajas.length===0&&canView&&(
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No hay cajas que coincidan con los filtros</TableCell>
                        </TableRow>
                      )}
                      {loading&&(
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={dashboardTheme.tableStyles.emptyCell}>Cargando cajas...</TableCell>
                        </TableRow>
                      )}
                      {!canView&&(
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No tienes permisos para ver cajas</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination component="div" count={cajas.length} page={page} onPageChange={(_event,newPage)=>setPage(newPage)} rowsPerPage={rowsPerPage} rowsPerPageOptions={[]} labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`} sx={dashboardTheme.tableStyles.pagination}/>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog open={detailOpen} onClose={()=>setDetailOpen(false)} fullWidth maxWidth="lg" fullScreen={isMobile} PaperProps={{sx:{bgcolor:dashboardTheme.card_background,color:dashboardTheme.text_color,border:`1px solid ${dashboardTheme.border_color}`,boxShadow:"none"}}}>
        <DialogTitle sx={{fontSize:20,fontWeight:700,color:dashboardTheme.primary_color}}>
          {selectedCaja?`Caja #${selectedCaja.id}`:"Detalle de caja"}
        </DialogTitle>
        <DialogContent dividers sx={{borderColor:dashboardTheme.border_color}}>
          {detailLoading?(
            <Typography sx={{py:4,textAlign:"center",color:secondaryText}}>Cargando detalle...</Typography>
          ):selectedCaja?(
            <>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={infoLabelSx}>Usuario apertura</Typography>
                  <Typography sx={infoValueSx}>{selectedCaja.usuario_apertura_nombre||"-"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={infoLabelSx}>Fecha apertura</Typography>
                  <Typography sx={infoValueSx}>{formatDateTime(selectedCaja.fecha_apertura)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={infoLabelSx}>Fecha cierre</Typography>
                  <Typography sx={infoValueSx}>{formatDateTime(selectedCaja.fecha_cierre)}</Typography>
                </Grid>

                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Monto inicial</Typography><Typography sx={infoValueSx}>${formatMoney(selectedCaja.monto_inicial)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Ventas</Typography><Typography sx={infoValueSx}>${formatMoney(selectedCaja.total_ventas_sistema)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Compras</Typography><Typography sx={infoValueSx}>${formatMoney(selectedCaja.total_compras_sistema)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Efectivo sistema</Typography><Typography sx={infoValueSx}>${formatMoney(selectedCaja.total_efectivo_sistema)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Transferencias</Typography><Typography sx={infoValueSx}>${formatMoney(selectedCaja.total_transferencia_sistema)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Ingresos manuales</Typography><Typography sx={infoValueSx}>${formatMoney(selectedCaja.total_ingresos_manuales)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Egresos manuales</Typography><Typography sx={infoValueSx}>${formatMoney(selectedCaja.total_egresos_manuales)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Efectivo esperado</Typography><Typography sx={infoValueSx}>${formatMoney(selectedCaja.efectivo_esperado_cierre)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Efectivo contado</Typography><Typography sx={infoValueSx}>{selectedCaja.efectivo_contado_cierre===null?"-":`$${formatMoney(selectedCaja.efectivo_contado_cierre)}`}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography sx={infoLabelSx}>Transferencia contada</Typography><Typography sx={infoValueSx}>{selectedCaja.transferencia_contada_cierre===null?"-":`$${formatMoney(selectedCaja.transferencia_contada_cierre)}`}</Typography></Grid>
                <Grid item xs={6} md={3}>
                  <Typography sx={infoLabelSx}>Diferencia</Typography>
                  <Typography sx={{...infoValueSx,color:selectedCaja.usuario_cierre&&selectedCaja.efectivo_contado_cierre!==null&&Number(selectedCaja.diferencia_efectivo||0)<0?"#D32F2F":dashboardTheme.text_color}}>
                    {selectedCaja.usuario_cierre&&selectedCaja.efectivo_contado_cierre!==null?`$${formatMoney(selectedCaja.diferencia_efectivo)}`:"-"}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{my:2,borderColor:dashboardTheme.border_color}}/>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <Typography sx={infoLabelSx}>Observación de apertura</Typography>
                  <Typography sx={infoValueSx}>{selectedCaja.observacion_apertura||"-"}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={infoLabelSx}>Observación de cierre</Typography>
                  <Typography sx={infoValueSx}>{selectedCaja.observacion_cierre||"-"}</Typography>
                </Grid>
              </Grid>

              <Typography sx={{fontSize:17,fontWeight:700,color:dashboardTheme.primary_color,mt:3,mb:1}}>Movimientos</Typography>

              <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
                <Box sx={dashboardTheme.tableStyles.tableArea}>
                  <TableContainer sx={{...dashboardTheme.tableStyles.container,maxHeight:380}}>
                    <Table size="small" stickyHeader sx={dashboardTheme.tableStyles.table}>
                      <TableHead>
                        <TableRow sx={dashboardTheme.tableStyles.headRow}>
                          <TableCell sx={dashboardTheme.tableStyles.headCell}>Fecha</TableCell>
                          <TableCell sx={dashboardTheme.tableStyles.headCell}>Tipo</TableCell>
                          <TableCell sx={dashboardTheme.tableStyles.headCell}>Origen</TableCell>
                          <TableCell sx={dashboardTheme.tableStyles.headCell}>Forma</TableCell>
                          <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:200}}>Concepto</TableCell>
                          <TableCell sx={dashboardTheme.tableStyles.headCell}>Referencia</TableCell>
                          <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Monto</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {movimientos.map(mov=>(
                          <TableRow key={mov.id} sx={dashboardTheme.tableStyles.bodyRow}>
                            <TableCell>{formatDateTime(mov.creado_en)}</TableCell>
                            <TableCell><Typography sx={{fontSize:13,fontWeight:600,color:mov.tipo==="EGRESO"?"#D32F2F":dashboardTheme.text_color}}>{mov.tipo}</Typography></TableCell>
                            <TableCell>{mov.origen}</TableCell>
                            <TableCell>{mov.forma_pago}</TableCell>
                            <TableCell>
                              <Typography sx={{fontSize:13.5,fontWeight:600,color:dashboardTheme.text_color}}>{mov.concepto||"-"}</Typography>
                              {mov.observacion&&<Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,mt:0.2}}>{mov.observacion}</Typography>}
                            </TableCell>
                            <TableCell>{mov.referencia||"-"}</TableCell>
                            <TableCell align="right">
                              <Typography sx={{fontSize:13.5,fontWeight:700,color:mov.tipo==="EGRESO"?"#D32F2F":dashboardTheme.primary_color}}>
                                {mov.tipo==="EGRESO"?"-":""}${formatMoney(mov.monto)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {movimientos.length===0&&(
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={dashboardTheme.tableStyles.emptyCell}>Esta caja no tiene movimientos</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </>
          ):null}
        </DialogContent>
        <DialogActions sx={{px:3,py:1.5}}>
          <Button onClick={()=>setDetailOpen(false)} sx={{textTransform:"none",fontWeight:700,color:dashboardTheme.button_color}}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
